import * as React from 'react';
import { Link, matchPath, useLocation } from 'react-router';
import { Avatar, Badge, BottomNavigation, BottomNavigationAction, Box, Collapse, Divider, Drawer, IconButton, List,  ListItem,  ListItemButton, ListItemButtonProps, ListItemIcon, ListItemText, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { AccountBalanceSharp, ChevronLeft, ChevronRight, ChromeReaderModeSharp, ContactSupport, EditNoteRounded, ExpandLess, ExpandMore, Face5, Feedback as FeedbackIcon, JoinInner, PersonSharp, Restore, SchoolSharp } from '@mui/icons-material';
import { grey } from '@mui/material/colors';

import { useRouter } from '@cvt/routing';
import { DialogContext, PermissionContext } from '@cvt/contexts';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { NavLink } from '@cvt/components/NavLink';

import { Authenticated, NotAuthenticated } from '@modules/Auth/components';
import { UserContext } from '@modules/Users/contexts';
import { useUserCrud } from '@modules/Users/hooks/useUserCrud';
import { useAssertionsResultsQuery } from '@modules/Assertions/hooks/useAssertionsResultsQuery';

import config from '@shared/config';
import { navigation } from '@shared/routes';
import { SettingsContext } from '@shared/contexts';
import { useMatchedRoute } from '@shared/hooks/useMatchedRoute';
import { ProfileMenu } from '@shared/components/ProfileMenu';
import { useLeaveReview } from '@shared/hooks/useLeaveReview';
import { ShareListItem } from '@shared/components/ShareListItem';


type NavigationItemProps = {
  item: CVT.Navigation.NavigationItem;
  level?: number;
  onCloseRequest: () => void;
  ListItemButtonProps?: Pick<ListItemButtonProps, 'sx'>;
}

type Props = React.PropsWithChildren<{
  open: boolean;
  onOpenRequest: () => void;
  onCloseRequest: () => void;
}>;

const NavigationItem: React.FC<NavigationItemProps> = ({ level = 0, item, onCloseRequest, ListItemButtonProps }) => {
  const { getPermission } = React.useContext(PermissionContext);
  const { sidebar } = React.useContext(SettingsContext);

  const [open, setOpen] = React.useState(false);

  const canView = React.useMemo(() => {
    return item.permission ? getPermission(item.permission) : true;
  }, [item, getPermission]);

  const allowedChildren = React.useMemo(() => {
    if (sidebar !== 'open') {
      return [];
    }
    return item.children?.filter(it => it.permission ? getPermission(it.permission) : true);
  }, [item, getPermission, sidebar]);

  const handleClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(!open);
  };

  const getRouteList = React.useCallback((node: CVT.Navigation.NavigationItem) => {
    let list: string[] = [];
    if (node.route) {
      list.push(node.route);

      node.children?.forEach(child => {
        const childList = getRouteList(child);
        list = [...list, ...childList];
      });
    };
    return list;
  }, []);

  const routes = React.useMemo(() => getRouteList(item), [getRouteList, item]);

  const { match } = useMatchedRoute(routes);

  React.useEffect(() => {
    if (match) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [match]);

  const itemContent = (
    <React.Fragment>
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      <ListItemText
        primary={item.text}
        secondary={item.secondaryText}
        slotProps={{ primary: { variant: 'body2', noWrap: true } }}
      />
      {allowedChildren && allowedChildren.length > 0 && (
        <IconButton onClick={handleClick} size="small">
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      )}
    </React.Fragment>
  );
  const itemChildren = (
    <React.Fragment>
      {allowedChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List
            component="div"
            disablePadding
            sx={{
              // @ts-ignore
              bgcolor: grey[level * 100],
            }}
          >
            {allowedChildren.map((child, idx) => (
              <NavigationItem
                level={level + 1}
                key={idx}
                item={child}
                onCloseRequest={onCloseRequest}
                ListItemButtonProps={{
                  sx: {
                    pl: Math.round(Math.log2(level + 2) * 4),
                  },
                }}
              />
            ))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );

  if (!canView) {
    return null;
  }
  if (item.divider) {
    return <Divider/>;
  }
  if (!!item.route) {
    return (
      <React.Fragment>
        <ListItemButton
          {...ListItemButtonProps}
          component={NavLink}
          to={item.route}
          onClick={onCloseRequest}
          disabled={item.disabled}
        >
          {itemContent}
        </ListItemButton>
        {itemChildren}
      </React.Fragment>
    );
  }
  if (!!item.onClick) {
    return (
      <React.Fragment>
        <ListItemButton
          {...ListItemButtonProps}
          disabled={item.disabled}
          onClick={() => [item.onClick && item.onClick(), onCloseRequest()]}
        >
          {itemContent}
        </ListItemButton>
        {itemChildren}
      </React.Fragment>
    );
  }
  return null;
};

export const NavigationDesktop: React.FC<Props> = ({ open, onCloseRequest }) => {
  const router = useRouter();
  const dictionary = useDictionary();
  const { user } = React.useContext(UserContext);
  const { openDialog, asyncConfirmation } = React.useContext(DialogContext);
  const { sidebar, toggleSidebar } = React.useContext(SettingsContext);

  const { resetAccount } = useUserCrud();

  const leaveReview = useLeaveReview();

  const handleResetAccount = React.useCallback(async () => {
    const userConfirmed = await asyncConfirmation({
      title: 'Reset Account',
      content: 'Are you sure you want to reset your account? This action cannot be undone.',
      confirmLabel: 'Reset',
      cancelLabel: 'Cancel',
    });
    if (!userConfirmed) return;
    await resetAccount.mutateAsync();
  }, [asyncConfirmation, resetAccount]);

  const sidebarMenuRoutes = React.useMemo(() => {
    return (navigation(router, dictionary).sidebar || []).filter(it => open ? true : !it.divider);
  }, [router, dictionary, open]);

  const authenticatedNavRoutes = React.useMemo(() => {
    return sidebarMenuRoutes.filter(it =>  it.requiresAuth || it.requiresAuth !== false);
  }, [sidebarMenuRoutes]);

  const notAuthenticatedNavRoutes = React.useMemo(() => {
    return sidebarMenuRoutes.filter(it => !it.requiresAuth);
  }, [sidebarMenuRoutes]);

  const drawer = (
    <React.Fragment>
      <Toolbar />
      <List>
        <Authenticated>
          {authenticatedNavRoutes.map((item, idx) => <NavigationItem key={idx} item={item} onCloseRequest={onCloseRequest} ListItemButtonProps={{ sx: { my: 1 } }} />)}
          {user?.ongoingFeedbackSession && (
            <NavigationItem
              item={{
                icon: <FeedbackIcon />,
                text: dictionary.menu.feedback,
                route: router.feedback.path,
              }}
              onCloseRequest={onCloseRequest}
              ListItemButtonProps={{ sx: { my: 1 } }}
            />
          )}
          {config.featureFlags.understandMatches && (
            <NavigationItem
              item={{
                icon: <JoinInner />,
                text: dictionary.menu.understandMatchesShort,
                route: router.understandMatches.path,
              }}
              onCloseRequest={onCloseRequest}
              ListItemButtonProps={{ sx: { my: 1 } }}
            />
          )}
          <NavigationItem
            item={{
              icon: <EditNoteRounded />,
              text: dictionary.menu.leaveReview,
              onClick: leaveReview,
            }}
            onCloseRequest={onCloseRequest}
            ListItemButtonProps={{ sx: { my: 1 } }}
          />
          {user?.archetype && (
            <ShareListItem
              archetype={user.archetype}
              archetypeDescription={user.archetypeDescription}
              label={dictionary.menu.share}
              onAfterShare={onCloseRequest}
            />
          )}
          <NavigationItem
            item={{
              icon: <ContactSupport />,
              text: dictionary.menu.contactUs,
              onClick: () => openDialog('supportContact'),
            }}
            onCloseRequest={onCloseRequest}
            ListItemButtonProps={{ sx: { my: 1 } }}
          />
          <NavigationItem
            item={{
              icon: <Restore />,
              text: dictionary.menu.resetAccount,
              onClick: handleResetAccount,
            }}
            onCloseRequest={onCloseRequest}
            ListItemButtonProps={{ sx: { my: 1 } }}
          />
        </Authenticated>
        <NotAuthenticated>
          {notAuthenticatedNavRoutes.map((item, idx) => <NavigationItem key={idx} item={item} onCloseRequest={onCloseRequest} ListItemButtonProps={{ sx: { my: 1 } }} />)}
        </NotAuthenticated>
      </List>
      <Authenticated>
        <Box mt="auto">
          <List>
            {user && (
              <ListItem
                disableGutters
                disablePadding
              >
                <ListItemButton component={NavLink} to={router.profile.path} onClick={onCloseRequest}>
                  <ListItemIcon>
                    <Avatar alt={user?.displayName} src={user?.profilePicture} sx={{ width: 24, height: 24, backgroundColor: 'transparent', color: 'inherit' }}>
                      <Face5/>
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText primary={user?.displayName} slotProps={{ primary: { noWrap: true, variant: 'body2' } }}/>
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Authenticated>
    </React.Fragment>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: sidebar === 'open' ? config.theme.drawerWidth : 55 },
        transition: 'width ease-in-out 0.4s',
        flexShrink: { sm: 0 },
        '&:hover #sidebar-toggle': {
          opacity: 1,
        },
      }}
      position="relative"
    >
      <Drawer
        variant="permanent"
        slotProps={{
          paper: {
            variant: 'elevation',
            sx: {
              boxSizing: 'border-box',
              width: sidebar === 'open' ? config.theme.drawerWidth : 55,
              overflowX: 'hidden',
              transition: 'width ease-in-out 0.4s',
              border: 'none',
              backgroundColor: 'background.default',
            },
          },
        }}
        sx={{
          display: { xs: 'none', sm: 'block' },
        }}
        open
      >
        {drawer}
      </Drawer>
      <IconButton
        id="sidebar-toggle"
        aria-label="sidebar toggle"
        color="inherit"
        size="small"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          opacity: 0,
          transition: 'all ease-in 0.2s',
          position: 'fixed',
          top: theme => theme.spacing(10.5),
          left: sidebar === 'open' ? config.theme.drawerWidth : 55,
          transform: 'translateX(-50%)',
          color: 'secondary.contrastText',
          backgroundColor: 'secondary.main',
          zIndex: theme => theme.zIndex.drawer + 1,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme => theme.palette.divider,

          '&:hover, &:focus': {
            backgroundColor: 'secondary.main',
          },
        }}
        onClick={toggleSidebar}
      >
        {sidebar === 'open' ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
    </Box>
  );
};

export const NavigationMobile: React.FC<Props> = () => {

  const dictionary = useDictionary();
  const location = useLocation();
  const router = useRouter();

  const { user } = React.useContext(UserContext);

  const { results } = useAssertionsResultsQuery();

  const [profileAnchor, setProfileAnchor] = React.useState<HTMLElement | null>(null);

  const pathname = React.useMemo(() => {
    if (matchPath(`${router.disciplines.path}/*`, location.pathname)) {
      return router.disciplines.path;
    }
    if (matchPath(`${router.majors.path}/*`, location.pathname)) {
      return router.majors.path;
    }
    if (matchPath(`${router.universities.path}/*`, location.pathname)) {
      return router.universities.path;
    }

    return location.pathname;
  }, [router, location.pathname]);

  return (
    <BottomNavigation
      showLabels
      value={pathname}
      sx={{
        backgroundColor: 'transparent',
        color: 'common.black',
      }}
    >
      <BottomNavigationAction
        label={dictionary.menu.dashboard}
        slotProps={{
          label: {
            sx: {
              whiteSpace: 'nowrap',
            },
          },
        }}
        icon={<PersonSharp/>}
        component={Link}
        to={{
          pathname: router.home.path,
        }}
        value={router.home.path}
        sx={{
          px: 1,
          minWidth: 75,
          color: 'inherit',
        }}
      />
      <BottomNavigationAction
        label={dictionary.menu.disciplines}
        icon={<Badge badgeContent={Math.min(2, results?.disciplines)} color="primary"><AccountBalanceSharp/></Badge>}
        component={Link}
        to={router.disciplines.path}
        value={router.disciplines.path}
        sx={{
          px: 1,
          minWidth: 75,
          color: 'inherit',
        }}
      />
      <BottomNavigationAction
        label={dictionary.menu.majors}
        icon={<Badge badgeContent={results.majors} color="primary"><ChromeReaderModeSharp/></Badge>}
        component={Link}
        to={router.majors.path}
        value={router.majors.path}
        sx={{
          px: 1,
          minWidth: 75,
          color: 'inherit',
        }}
      />
      <BottomNavigationAction
        label={dictionary.menu.universities}
        icon={<Badge badgeContent={results.universities} color="primary"><SchoolSharp/></Badge>}
        component={Link}
        to={router.universities.path}
        value={router.universities.path}
        sx={{
          px: 1,
          minWidth: 75,
          color: 'inherit',
        }}
      />
      <BottomNavigationAction
        label={user?.firstName || dictionary.menu.settings}
        icon={(
          <Avatar alt={user?.displayName} src={user?.profilePicture} sx={{ width: 24, height: 24, backgroundColor: 'transparent', color: 'inherit' }}>
            <Face5/>
          </Avatar>
        )}
        onClick={(e) => setProfileAnchor(e.currentTarget as HTMLElement)}
        sx={{
          px: 1,
          minWidth: 75,
          color: 'inherit',
        }}
      />
      <ProfileMenu
        anchorEl={profileAnchor}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </BottomNavigation>
  );
};

export const Navigation: React.FC<Props> = props => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (isDesktop) {
    return <NavigationDesktop {...props} />;
  }

  return <NavigationMobile {...props} />;
};
